import { ApolloError, gql, useQuery } from "@apollo/client";
import { strings } from "@resource/common";
import faker from "faker";
import { OrganizationForModuleResolution } from "generated/schemaTypes";
import _, { first, map } from "lodash";
import {
  defaultCandidateName,
  defaultCompanyName,
  defaultEvents,
} from "utils/legacy/module-generators/defaults";
import { Overrides } from "utils/legacy/module-generators/types";

import { GuidePreviewContext } from "../types";

const fakeGuideId = faker.datatype.uuid();
const fakeJobId = faker.datatype.uuid();
const fakeCandidateId = faker.datatype.uuid();

const ORGANIZATION_FOR_MODULE_RESOLUTION = gql`
  query OrganizationForModuleResolution {
    currentUserPrisma {
      id
      currentOrganization {
        id
        name
        missionStatement
        theme {
          primaryColor
          secondaryColor
        }
        values {
          id
          title
          description
        }
        customer {
          id
          addToCalendarEnabled
        }
      }
    }
  }
`;

interface Stage {
  id: string;
  position: number;
  atsJobStageId?: string;
}

type ExamplePreviewContext = {
  context?: GuidePreviewContext;
  loading: boolean;
  error: ApolloError | undefined;
};

export default function useExampleCandidateModuleContext({
  stages,
  overrides,
}: {
  stages: Stage[];
  overrides?: Overrides;
}): ExamplePreviewContext {
  const { data, loading, error } = useQuery<OrganizationForModuleResolution>(
    ORGANIZATION_FOR_MODULE_RESOLUTION
  );

  if (loading || error) {
    return {
      loading,
      error,
      context: undefined,
    };
  }

  const organization = data?.currentUserPrisma?.currentOrganization;

  if (!organization) {
    throw new Error("No organization found when one was expected");
  }

  const candidateFirstName =
    overrides?.candidateFirstName ??
    strings.splitName(defaultCandidateName).firstName;
  const candidateFullName = overrides?.candidateName ?? defaultCandidateName;
  const recruiterFirstName = "Maddie";
  const recruiterFullName = "Maddie Tierney";

  const coordinatorFirstName = recruiterFirstName;
  const coordinatorFullName = recruiterFullName;
  const companyName = overrides?.companyName || defaultCompanyName;
  const jobName = overrides?.roleName ?? "Senior Software Engineer";
  const fakeGreenhouseJobUrl = "https://fake-greenhouse-job-description.com";

  const guideVariableReplacements = {
    "candidate-first-name": candidateFirstName,
    "candidate-full-name": candidateFullName,
    "recruiter-first-name": recruiterFirstName,
    "recruiter-full-name": recruiterFullName,
    "coordinator-first-name": coordinatorFirstName,
    "coordinator-full-name": coordinatorFullName,
    "company-name": companyName,
    "job-role-name": jobName,
    "greenhouse-job-link": fakeGreenhouseJobUrl,
  };

  const context = {
    id: fakeGuideId,
    publicUrl: "https://guide.co",
    recruiterEmailAddress: "support@guide.co",
    recruiterFirstName: "Maddie",
    interviews: _(stages)
      .flatMap((stage) =>
        _.map(defaultEvents, (interview) => {
          const firstInterviewer = first(interview.interviewers);
          const interviewerFullNames = map(interview.interviewers, (i) =>
            strings.joinNames(i.firstName, i.lastName)
          );

          return {
            ...interview,
            atsJobStageId: stage.atsJobStageId,
            startTime: new Date(interview.startTime),
            endTime: new Date(interview.endTime),
            description: interview.description || "",
            variableReplacements: {
              ...guideVariableReplacements,
              "all-interviewers-full-names": strings.joinWithFinalSeparator(
                interviewerFullNames,
                "and"
              ),
              "all-interviewers-first-names": strings.joinWithFinalSeparator(
                map(interview.interviewers, "firstName"),
                "and"
              ),
              "first-interviewer-first-name": firstInterviewer?.firstName,
              "first-interviewer-full-name": strings.joinNames(
                firstInterviewer?.firstName,
                firstInterviewer?.lastName
              ),
              "first-interviewer-title": firstInterviewer?.title,
            },
          };
        })
      )
      .value(),
    stages: _(stages)
      .sortBy("position")
      .map((stage) => ({
        id: stage.id,
        atsJobStageId: stage.atsJobStageId ?? "",
        variableReplacements: guideVariableReplacements,
        customData: {
          hiringTeam: [],
          customVariables: {},
        },
        needsFeedback: false,
      }))
      .value(),
    organization: {
      id: organization.id,
      name: organization.name,
      addToCalendarEnabled: organization.customer.addToCalendarEnabled,
      values: organization.values,
      missionStatement: organization.missionStatement,
    },
    job: {
      id: fakeJobId,
      name: jobName,
      greenhouseJobUrl: fakeGreenhouseJobUrl,
    },
    candidate: {
      id: fakeCandidateId,
      imageUrl: "",
      firstName: candidateFirstName,
      fullName: candidateFullName,
    },
  };

  return {
    loading,
    error,
    context,
  };
}
